import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  useKeyDown,
} from '@makeably/creativex-design-system';
import { getExplorerPath } from 'components/internal/explorer/shared';
import { NULL_LABEL } from 'components/internal/shared';
import Collapsible from 'components/molecules/Collapsible';
import { clampToRange } from 'utilities/number';
import { internalVersionPath } from 'utilities/routes';
import {
  splitLines,
  timestampToDateTime,
  titleize,
} from 'utilities/string';
import styles from './Version.module.css';

const versionProps = PropTypes.shape({
  event: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  itemId: PropTypes.number.isRequired,
  object: PropTypes.string,
  objectChanges: PropTypes.string.isRequired,
  timestamp: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  userCompany: PropTypes.string,
  userEmail: PropTypes.string,
  userName: PropTypes.string,
});

const propTypes = {
  versionId: PropTypes.number.isRequired,
  versions: PropTypes.arrayOf(versionProps).isRequired,
};

function parseObject(object) {
  if (!object) return null;

  return splitLines(object).slice(1).map((text) => ({ text }));
}

function parseChanges(object) {
  if (!object) return null;

  const changePrefix = '-';
  const continuePrefix = '  ';
  const prefixLength = 2;
  const lines = splitLines(object).slice(1);
  let isAdd = true;

  return lines.map((line) => {
    const isChange = line.startsWith(changePrefix);
    const isContinue = line.startsWith(continuePrefix);

    if (!isChange && !isContinue) {
      isAdd = true;

      return { text: line };
    }

    isAdd = isChange ? !isAdd : isAdd;

    return {
      classes: isAdd ? styles.added : styles.removed,
      text: line.slice(prefixLength),
    };
  });
}

function getAction(event) {
  switch (event) {
    case 'create':
      return 'Created';
    case 'destroy':
      return 'Destroyed';
    default:
      return 'Updated';
  }
}

function getVersionId(id) {
  return `version::${id}`;
}

function scrollIntoView(id) {
  const elem = document.getElementById(getVersionId(id));

  elem?.scrollIntoView({ inline: 'nearest' });
}

function renderEvent(version, currentId, onSelect) {
  const {
    event,
    id,
    timestamp,
  } = version;
  const isCurrent = id === currentId;
  const classes = classNames(
    't-body-1',
    styles.event,
    {
      't-subtitle': isCurrent,
      [styles.current]: isCurrent,
    },
  );

  return (
    <button
      key={id}
      className={classes}
      id={getVersionId(id)}
      type="button"
      onClick={onSelect}
    >
      <div>{ `${getAction(event)}` }</div>
      <div>{ `${timestampToDateTime(timestamp)}` }</div>
    </button>
  );
}

function renderDetail(label, value) {
  return (
    <div className="u-flexRow u-gap-8">
      <span>
        { label }
        :
      </span>
      <h6>{ value ?? NULL_LABEL }</h6>
    </div>
  );
}
function renderObject(label, lines, isInitiallyOpen) {
  if (!lines) return null;

  return (
    <Collapsible
      header={label}
      isInitiallyOpen={isInitiallyOpen}
    >
      <div className={`u-scrollShadowRight ${styles.object}`}>
        { lines.map(({ text, classes }, index) => (
          <div key={index} className={classes}> { /* eslint-disable-line */ }
            { text }
          </div>
        )) }
      </div>
    </Collapsible>
  );
}

function Version({
  versionId,
  versions,
}) {
  const [index, setIndex] = useState(versions.findIndex(({ id }) => id === versionId));
  const {
    event,
    id,
    itemId,
    object,
    objectChanges,
    timestamp,
    type,
    userCompany,
    userName,
    userEmail,
  } = versions[index];

  useEffect(() => scrollIntoView(id), []);

  const handleIndexChange = (inc) => {
    const nextIndex = clampToRange(0, index + inc, versions.length - 1);
    const nextId = versions[nextIndex].id;

    setIndex(nextIndex);
    scrollIntoView(nextId);
    window.history.replaceState(null, '', internalVersionPath(nextId));
  };

  useKeyDown('ArrowLeft', () => handleIndexChange(-1));
  useKeyDown('ArrowRight', () => handleIndexChange(1));

  const lastLines = useMemo(() => parseObject(object), [object]);
  const changedLines = useMemo(() => parseChanges(objectChanges), [objectChanges]);

  return (
    <Card className="u-flexColumn u-gap-24">
      <h4>
        <a href={getExplorerPath(type, itemId)}>{ `${type} (${itemId})` }</a>
        <span>{ ' Change Log' }</span>
      </h4>
      <div>
        <div className="u-flexRow u-gap-8 u-marginBottom-8">
          <h5>Timeline</h5>
          <span>{ `(Change ${index + 1} of ${versions.length})` }</span>
        </div>
        <div className={styles.timeline}>
          <Button
            disabled={index === 0}
            iconLeft="arrowLeft"
            variant="round"
            onClick={() => handleIndexChange(-1)}
          />
          <div className={styles.events}>
            <div className={styles.fadeIn} />
            { versions.map((ver, idx) => renderEvent(ver, id, () => setIndex(idx))) }
            <div className={styles.fadeOut} />
          </div>
          <Button
            disabled={index === versions.length - 1}
            iconLeft="arrowRight"
            variant="round"
            onClick={() => handleIndexChange(1)}
          />
        </div>
      </div>
      <div>
        <h5 className="u-marginBottom-8">Details</h5>
        <div className="u-flexColumn u-gap-8">
          { renderDetail('Time Changed', timestampToDateTime(timestamp)) }
          { renderDetail('Event', titleize(event)) }
          { renderDetail('User', userName) }
          { renderDetail('User Email', userEmail) }
          { renderDetail('User Company', userCompany) }
        </div>
      </div>
      { renderObject('Last Version', lastLines, false) }
      { renderObject('Changes', changedLines, true) }
    </Card>
  );
}

Version.propTypes = propTypes;

export default Version;
